import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Select } from "antd";
import axios from "axios";
import Swal from "sweetalert2";
import JoditEditor, { Jodit } from "jodit-react";
import { Button, CircularProgress } from "@mui/material";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AddComment } from "@mui/icons-material";
import Cookies from "universal-cookie";

const { Option } = Select;

/* =========================
   CONFIGURACIÓN JODIT
========================= */

Jodit.lang.es = {
  ...Jodit.lang.es,
  "The pasted content looks like HTML. Keep as HTML?":
    "El contenido parece ser HTML. ¿Deseas mantenerlo como está?",
};

const useJoditConfig = () =>
  useMemo(
    () => ({
      readonly: false,
      language: "es",
      toolbarAdaptive: false,
      toolbarSticky: false,
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      iframe: false,
      useSearch: false,
      uploader: { insertImageAsBase64URI: true },
      askBeforePasteFromWord: false,
      askBeforePasteHTML: false,
      defaultActionOnPaste: "insert_as_html",
    }),
    []
  );

/* =========================
   COMPONENTE
========================= */

const CrearGestionModal = ({ id, onSuccess }) => {
  const cookie = new Cookies();
  const editorConfig = useJoditConfig();

  const getCurrentDateTime = () => {
    const now = new Date();
    return new Intl.DateTimeFormat("sv-SE", {
      timeZone: "America/Bogota",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .format(now)
      .replace(" ", "T");
  };

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [estados, setEstados] = useState([]);

  const [formData, setFormData] = useState({
    idSolicitud: id,
    descripcion: "",
    fechaCreacion: getCurrentDateTime(),
    idUsuario: cookie.get("id"),
    estado: "",
  });

  /* =========================
     EFFECTS
  ========================= */

  useEffect(() => {
    setFormData((prev) => ({ ...prev, idSolicitud: id }));
  }, [id]);

  useEffect(() => {
    const fetchEstados = async () => {
      try {
        const { data } = await axios.get(
          "https://contratosback.azurewebsites.net/api/v1/ObtenerEstados"
        );
        setEstados(data.filter((e) => e.name !== "NUEVO"));
      } catch (error) {
        console.error("Error obteniendo estados", error);
      }
    };

    fetchEstados();
  }, []);

  /* =========================
     HANDLERS
  ========================= */

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    if (loading) return;

    setOpen(false);
    setFormData((prev) => ({
      ...prev,
      descripcion: "",
      estado: "",
      fechaCreacion: getCurrentDateTime(),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.descripcion.trim()) {
      Swal.fire("Error", "La descripción no puede estar vacía", "error");
      return;
    }

    setLoading(true);

    try {
      if (formData.estado) {
        await axios.patch(
          `https://contratosback.azurewebsites.net/api/v1/ActualizaSolicitudJuridica/${id}`,
          {
            idSolicitud: id,
            idEstado: formData.estado,
            informacionAdicional: "Gestion",
          },
          { headers: { "Content-Type": "application/json" } }
        );
      }

      await axios.post("https://contratosback.azurewebsites.net/api/v1/CreaGestion", formData, {
        headers: { "Content-Type": "application/json" },
      });

      Swal.fire("Éxito", "Estado actualizado y gestión creada correctamente", "success");

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error en la operación", error);
      Swal.fire("Error", "Hubo un problema en la operación", "error");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     RENDER
  ========================= */

  return (
    <>
      <OverlayTrigger placement="top" overlay={<Tooltip>Nueva Gestión</Tooltip>}>
        <Button variant="contained" color="primary" onClick={handleOpen}>
          <AddComment style={{ color: "white" }} />
        </Button>
      </OverlayTrigger>

      <Modal
        title="Crear Gestión"
        open={open}
        onCancel={handleClose}
        footer={null}
        width={1100}
        forceRender
      >
        <Form layout="vertical" onFinish={handleSubmit}>
          <Form.Item label="Descripción">
            {open && (
              <JoditEditor
                key={`jodit-${id}-${open}`}
                config={editorConfig}
                value={formData.descripcion}
                onBlur={(content) =>
                  setFormData((prev) => ({
                    ...prev,
                    descripcion: content,
                  }))
                }
                onChange={() => {}}
              />
            )}
          </Form.Item>

          <Form.Item label="Estado de la Solicitud">
            <Select
              value={formData.estado}
              onChange={(value) => setFormData((prev) => ({ ...prev, estado: value }))}
              placeholder="Seleccione un estado"
            >
              {estados.map((estado) => (
                <Option key={estado.id} value={estado.id}>
                  {estado.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="submit"
              disabled={loading}
              style={{
                border: "2px solid #1976d2",
                color: "#1976d2",
              }}
            >
              {loading ? <CircularProgress size={22} /> : "Crear y Actualizar Estado"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CrearGestionModal;
